/**
 * src/components/CookieConsentBanner.js
 *
 * React Component Module.
 *
 * Purpose: React UI module containing one or more components and local UI helpers.
 *
 * Data flow (high level):
 * - UI/Caller -> this module -> downstream helpers/services -> state/response.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const CONSENT_STORAGE_KEY = 'cmpConsentState';
const CONSENT_COOKIE_KEY = 'cmpConsentState';
const LEGACY_CONSENT_KEY = 'cookieConsent';

// Keep consent for 1 year; this is a strictly-necessary preference cookie.
const CONSENT_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

const CONSENT_ACCEPT_ALL = {
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
  analytics_storage: 'granted',
  functionality_storage: 'granted',
  personalization_storage: 'granted',
  security_storage: 'granted',
};

const CONSENT_ESSENTIAL_ONLY = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  functionality_storage: 'granted',
  personalization_storage: 'denied',
  security_storage: 'granted',
};

/**
 * Safe Json Parse.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const safeJsonParse = (value) => {
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
};

/**
 * Get cookie domain used to share consent across our main domain + subdomains.
 *
 * Note: localStorage is origin-scoped and won't persist across `www.` vs apex domains.
 * A domain cookie lets us persist the banner decision across those hosts.
 */
const getConsentCookieDomain = () => {
  try {
    const hostname = window?.location?.hostname;
    if (!hostname || typeof hostname !== 'string') return null;

    // Hardcode our production domain. Avoid attempting to derive eTLD+1 (co.uk, etc.).
    if (hostname === 'speakable.online' || hostname.endsWith('.speakable.online')) {
      return '.speakable.online';
    }

    return null;
  } catch (e) {
    return null;
  }
};

/**
 * Get Cookie.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  try {
    const cookies = document.cookie ? document.cookie.split(';') : [];
    for (const part of cookies) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const eqPos = trimmed.indexOf('=');
      const key = eqPos > -1 ? trimmed.substring(0, eqPos) : trimmed;
      if (key !== name) continue;
      const value = eqPos > -1 ? trimmed.substring(eqPos + 1) : '';
      try {
        return decodeURIComponent(value);
      } catch (e) {
        return value;
      }
    }
  } catch (e) {}
  return null;
};

/**
 * Set Cookie.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const setCookie = (
  name,
  value,
  maxAgeSeconds = CONSENT_COOKIE_MAX_AGE_SECONDS
) => {
  if (typeof document === 'undefined') return;
  try {
    const parts = [
      `${name}=${encodeURIComponent(String(value))}`,
      'path=/',
      `max-age=${maxAgeSeconds}`,
      'samesite=lax',
    ];

    // Secure cookies only over HTTPS.
    if (typeof window !== 'undefined' && window?.location?.protocol === 'https:') {
      parts.push('secure');
    }

    const domain = typeof window !== 'undefined' ? getConsentCookieDomain() : null;
    if (domain) parts.push(`domain=${domain}`);

    document.cookie = parts.join(';');
  } catch (e) {}
};

/**
 * Safe Consent Parse.
 *
 * Supports both raw JSON strings (localStorage) and URL-encoded JSON (cookies).
 */
const safeConsentParse = (value) => {
  if (!value || typeof value !== 'string') return null;

  const parsed = safeJsonParse(value);
  if (parsed) return parsed;

  try {
    const decoded = decodeURIComponent(value);
    if (!decoded || decoded === value) return null;
    return safeJsonParse(decoded);
  } catch (e) {
    return null;
  }
};

const CONSENT_KEYS = [
  'ad_storage',
  'analytics_storage',
  'functionality_storage',
  'personalization_storage',
  'security_storage',
];

/**
 * Is Consent Value.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const isConsentValue = (value) => value === 'granted' || value === 'denied';

// Stored state might come from older versions or other scripts; validate minimally so we
// don't hide the banner for unrelated objects.
/**
 * Looks Like Consent State.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const looksLikeConsentState = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

  const hasAnyKey = CONSENT_KEYS.some((key) =>
    Object.prototype.hasOwnProperty.call(value, key)
  );
  if (!hasAnyKey) return false;

  for (const key of CONSENT_KEYS) {
    if (
      Object.prototype.hasOwnProperty.call(value, key) &&
      !isConsentValue(value[key])
    ) {
      return false;
    }
  }

  return true;
};

/**
 * Apply Consent.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const applyConsent = (consent, source = 'banner') => {
  if (typeof window === 'undefined' || !consent) return;

  try {
    window.__cmpConsentState = consent;
  } catch (e) {}

  try {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', consent);
    } else if (window.dataLayer?.push) {
      // Queue the update for when GTM/gtag loads.
      window.dataLayer.push(['consent', 'update', consent]);
    }
  } catch (e) {}

  try {
    window.dataLayer?.push({
      event: 'cmp_consent_update',
      consent,
      consentSource: source,
    });
  } catch (e) {}

  try {
    window.dispatchEvent(new CustomEvent('cmp:consent', { detail: consent }));
  } catch (e) {}
};

/**
 * Cookie Consent Banner.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const CookieConsentBanner = () => {
  const { t } = useTranslation('global');
  const [isVisible, setIsVisible] = useState(false);

  const isConversionLanding = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const pathname = window.location?.pathname || '';
    return (
      pathname === '/lekcja-probna' ||
      pathname === '/freelesson' ||
      pathname === '/book-trial-lesson'
    );
  }, []);

  const isAuthRoute = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const pathname = window.location?.pathname || '';
    return (
      /^\/(?:new-login|new-signup|mobile-signin|counsellor-login)(?:[/?#]|$)/i.test(pathname) ||
      /^\/trial-result\/[^/?#]+(?:[/?#]|$)/i.test(pathname)
    );
  }, []);

  const isDashboardRoute = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const pathname = window.location?.pathname || '';
    return /^\/(?:Admin-Dashboard|Teacher-dashboard|Student-dashboard|Accountant-Dashboard|Counsellor-Dashboard)(?:[/?#]|$)/i.test(pathname);
  }, []);

  const copy = useMemo(() => {
    const fallback = {
      title: 'Cookie consent',
      message:
        'This site uses cookies to improve your experience.',
      compactMessage: 'Essential cookies are always active. Optional analytics and marketing cookies require consent.',
      accept: 'Accept',
      decline: 'Decline',
    };

    const fromI18n = t('cookieConsent', { returnObjects: true });
    // react-i18next returns a string for missing keys; guard against that.
    if (!fromI18n || typeof fromI18n !== 'object') return fallback;
    return {
      title: fromI18n.title || fallback.title,
      message: fromI18n.message || fallback.message,
      compactMessage: fromI18n.compactMessage || fallback.compactMessage,
      accept: fromI18n.accept || fallback.accept,
      decline: fromI18n.decline || fallback.decline,
    };
  }, [t]);

  useEffect(() => {
    let stored = null;
    try {
      stored = window?.localStorage?.getItem(CONSENT_STORAGE_KEY);
    } catch (e) {
      stored = null;
    }

    let parsed = stored ? safeConsentParse(stored) : null;

    // Fallback: read consent from cookie (supports www/apex host switches and storage-blocked browsers).
    if (!looksLikeConsentState(parsed)) {
      const fromCookie = getCookie(CONSENT_COOKIE_KEY);
      parsed = fromCookie ? safeConsentParse(fromCookie) : null;
      if (looksLikeConsentState(parsed)) {
        // Best-effort sync back into localStorage for parts of the app that read it.
        try {
          window?.localStorage?.setItem(CONSENT_STORAGE_KEY, JSON.stringify(parsed));
          window?.localStorage?.setItem(
            LEGACY_CONSENT_KEY,
            parsed?.analytics_storage === 'granted' ? 'true' : 'false'
          );
        } catch (e) {}
      }
    }

    // Legacy boolean fallback (older versions used only `cookieConsent=true/false`).
    if (!looksLikeConsentState(parsed)) {
      let legacy = null;
      try {
        legacy = window?.localStorage?.getItem(LEGACY_CONSENT_KEY);
      } catch (e) {
        legacy = null;
      }
      if (!legacy) legacy = getCookie(LEGACY_CONSENT_KEY);

      if (legacy === 'true') parsed = CONSENT_ACCEPT_ALL;
      if (legacy === 'false') parsed = CONSENT_ESSENTIAL_ONLY;

      if (looksLikeConsentState(parsed)) {
        try {
          window?.localStorage?.setItem(CONSENT_STORAGE_KEY, JSON.stringify(parsed));
        } catch (e) {}
        setCookie(CONSENT_COOKIE_KEY, JSON.stringify(parsed));
      }
    }

    if (looksLikeConsentState(parsed)) {
      // Consent already decided; do not show the banner.
      setIsVisible(false);
      return;
    }

    // Clean up invalid/migrated state so we don't get stuck with a hidden banner.
    try {
      if (stored != null) window?.localStorage?.removeItem(CONSENT_STORAGE_KEY);
    } catch (e) {}

    // If an external CMP (IAB TCF) is present (e.g. consentmanager.net), avoid showing
    // our own banner to prevent double-consent UI. The external CMP will drive consent
    // updates via `__tcfapi` and our `public/index.html` bridge.
    try {
      if (typeof window?.__tcfapi === 'function') {
        setIsVisible(false);
        return;
      }
    } catch (e) {}

    // Authenticated dashboards are operational workspace UI, not a marketing
    // surface. Do not cover role flows with a consent overlay; keep optional
    // storage denied until the user makes a consent choice on public/auth pages.
    if (isDashboardRoute) {
      applyConsent(CONSENT_ESSENTIAL_ONLY, 'dashboard-default');
      setIsVisible(false);
      return;
    }

    setIsVisible(true);
  }, [isDashboardRoute]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (isVisible) {
      document.body.dataset.cookieBannerOpen = 'true';
    } else {
      delete document.body.dataset.cookieBannerOpen;
    }
    return () => {
      delete document.body.dataset.cookieBannerOpen;
    };
  }, [isVisible]);

    /**
   * Persist And Apply.
   *
   * React component or UI helper used to render and manage local UI interactions.
   */
   const persistAndApply = (consent) => {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
      localStorage.setItem(
        LEGACY_CONSENT_KEY,
        consent?.analytics_storage === 'granted' ? 'true' : 'false'
      );
    } catch (e) {}

    // Also persist into a first-party cookie so the decision survives host switches (www vs apex)
    // and environments that block localStorage access.
    setCookie(CONSENT_COOKIE_KEY, JSON.stringify(consent));
    setCookie(
      LEGACY_CONSENT_KEY,
      consent?.analytics_storage === 'granted' ? 'true' : 'false'
    );
    applyConsent(consent);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  const bannerMessage = isDashboardRoute ? copy.compactMessage : copy.message;

  const shellClassName = isConversionLanding
    ? 'fixed inset-x-0 bottom-3 md:bottom-4 z-[80] px-3 pointer-events-none'
    : isAuthRoute
      ? 'static z-[40] px-4 pb-5 pt-0 sm:fixed sm:inset-x-0 sm:bottom-0 sm:z-[80] sm:p-4'
      : isDashboardRoute
        ? 'fixed left-3 right-3 bottom-[5.75rem] z-[80] pointer-events-none sm:left-auto sm:right-4 sm:bottom-4 sm:w-[24rem]'
        : 'fixed inset-x-0 bottom-[5.25rem] sm:bottom-0 z-[80] p-3 sm:p-4';
  const panelClassName = isConversionLanding
    ? 'pointer-events-auto mx-auto max-w-3xl rounded-xl border border-gray-200 bg-white/95 backdrop-blur shadow-xl px-3 py-2.5 sm:px-4'
    : isAuthRoute
      ? 'mx-auto max-w-md rounded-2xl border border-gray-200 bg-white shadow-lg p-4 sm:max-w-5xl sm:bg-white/95 sm:backdrop-blur sm:shadow-2xl sm:p-5'
      : isDashboardRoute
        ? 'pointer-events-auto ml-auto rounded-xl border border-gray-200 bg-white/95 backdrop-blur shadow-xl p-3'
        : 'mx-auto max-w-5xl rounded-2xl border border-gray-200 bg-white/95 backdrop-blur shadow-2xl p-4 sm:p-5';
  const contentClassName = isConversionLanding
    ? 'flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'
    : isAuthRoute
      ? 'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'
      : isDashboardRoute
        ? 'flex flex-col gap-2'
        : 'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between';
  const titleClassName = isConversionLanding
    ? 'text-xs sm:text-sm font-semibold text-gray-900'
    : isDashboardRoute
      ? 'text-sm font-semibold text-gray-900'
      : 'text-sm sm:text-base font-semibold text-gray-900';
  const messageClassName = isConversionLanding
    ? 'text-[11px] sm:text-xs leading-snug text-gray-600 mt-0.5'
    : isDashboardRoute
      ? 'text-xs leading-snug text-gray-600 mt-0.5'
      : 'text-xs sm:text-sm text-gray-600 mt-1';
  const actionsClassName = isConversionLanding
    ? 'grid grid-cols-2 gap-1.5 sm:flex sm:shrink-0 sm:gap-2'
    : isDashboardRoute
      ? 'grid grid-cols-2 gap-2'
      : 'flex flex-col sm:flex-row gap-2 sm:gap-3';
  const secondaryButtonClassName = isConversionLanding
    ? 'px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors text-xs font-medium'
    : 'px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium';
  const primaryButtonClassName = isConversionLanding
    ? 'px-2.5 py-1.5 rounded-lg bg-[#113F67] text-white hover:bg-[#34699A] transition-colors text-xs font-semibold'
    : 'px-4 py-2 rounded-xl bg-[#113F67] text-white hover:bg-[#34699A] transition-colors text-sm font-semibold';

  return (
    <div
      className={shellClassName}
      role="dialog"
      aria-live="polite"
      aria-label={copy.title}
    >
      <div className={panelClassName}>
        <div className={contentClassName}>
          <div className="min-w-0">
            <div className={titleClassName}>
              {copy.title}
            </div>
            <div className={messageClassName}>
              {bannerMessage}
            </div>
          </div>

          <div className={actionsClassName}>
            <button
              type="button"
              onClick={() => persistAndApply(CONSENT_ESSENTIAL_ONLY)}
              className={secondaryButtonClassName}
            >
              {copy.decline}
            </button>
            <button
              type="button"
              onClick={() => persistAndApply(CONSENT_ACCEPT_ALL)}
              className={primaryButtonClassName}
            >
              {copy.accept}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
